import { Star, Shield, Rocket, LogIn, PlayCircle, BookOpen, Trophy } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuthModal } from "@/hooks/useAuthModal";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserAccessContext, resolveAuthenticatedHome, UserAccessContext } from "@/lib/access";

const HeroSection = () => {
  const { openSignup, openLogin } = useAuthModal();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [access, setAccess] = useState<UserAccessContext | null>(null);

  useEffect(() => {
    if (user) {
      getUserAccessContext(user.id).then(setAccess);
    }
  }, [user]);

  const stats = [
    { value: "50K+", label: t("hero.stat1") },
    { value: "120+", label: t("hero.stat2") },
    { value: "95%", label: t("hero.stat3") },
  ];

  return (
    <section className="relative min-h-dvh flex items-center pt-24 sm:pt-36 pb-10 sm:pb-16 overflow-hidden">
      <div className="absolute inset-0 bg-background" aria-hidden="true" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" style={{ contain: "layout paint size" }}>
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-25 animate-float" style={{ background: "var(--gradient-hero)", filter: "blur(90px)", contain: "layout paint size", willChange: "transform" }} />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full opacity-20 animate-float-2" style={{ background: "var(--gradient-orange)", filter: "blur(90px)", contain: "layout paint size", willChange: "transform" }} />
        <div className="absolute top-1/2 -left-20 w-72 h-72 rounded-full opacity-15 animate-float-3" style={{ background: "var(--gradient-pink)", filter: "blur(80px)", contain: "layout paint size", willChange: "transform" }} />
        <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "radial-gradient(circle, hsl(var(--primary)) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col gap-7 items-center text-center lg:items-start lg:text-left">
            <div className="badge-new inline-flex w-fit">
              <span>✨</span>
              <span>{t("hero.badge")}</span>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.08] tracking-tight">
              <span className="text-foreground">{t("hero.title1")}</span>
              <br />
              <span className="relative inline-block text-foreground">
                <span className="relative z-10">{t("hero.title2")}</span>
                <svg
                  className="absolute -bottom-1 left-0 w-[110%] -ml-[5%] h-3 sm:h-4 -z-10"
                  viewBox="0 0 220 16"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12 C 60 -2, 120 18, 180 12 C 200 8, 210 14, 216 12"
                    stroke="hsl(var(--accent))"
                    strokeWidth="5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              <span className="text-foreground">{t("hero.title3")}</span>
              <span className="text-gradient">{t("hero.title4")}</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground font-medium leading-relaxed max-w-xl mx-auto lg:mx-0" dangerouslySetInnerHTML={{ __html: t("hero.subtitle") }} />

            <div className="flex flex-col sm:flex-row flex-wrap gap-1.5 md:gap-3 justify-center lg:justify-start w-full">
              {user ? (
                <>
                  {access?.isSuperAdmin && (
                    <button
                      onClick={() => navigate("/admin")}
                      className="btn-hero text-xs w-full sm:w-auto min-h-[32px] rounded-xl px-3 py-1.5 md:text-base md:min-h-[48px] md:rounded-2xl md:px-6 md:py-3.5 border-l-4 border-l-destructive"
                      style={{ background: "hsl(var(--destructive))" }}
                    >
                      <Shield className="w-3 h-3 md:w-4 md:h-4" /> Console Admin
                    </button>
                  )}
                  <button onClick={() => {
                    const home = resolveAuthenticatedHome(access ?? { onboardingCompleted: true, profileRole: null, adminRole: null, isAdminLevel: false, isSuperAdmin: false });
                    if (access?.profileRole === "parent") {
                      navigate("/parent-dashboard");
                      return;
                    }
                    navigate(home === "/teacher-dashboard" ? "/teacher-dashboard#dashboard" : home);
                  }} className="btn-hero text-xs w-full sm:w-auto min-h-[32px] rounded-xl px-3 py-1.5 md:text-base md:min-h-[48px] md:rounded-2xl md:px-6 md:py-3.5"><BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4" /> {access?.profileRole === "parent" ? "Continuer vers le Tableau de bord" : access?.profileRole === "enseignant" ? "Aller dans votre interface" : t("hero.ctaContinue")}</button>
                </>
              ) : (
                <>
                  <button onClick={openSignup} className="btn-hero text-xs w-full sm:w-auto min-h-[32px] rounded-xl px-3 py-1.5 md:text-base md:min-h-[48px] md:rounded-2xl md:px-6 md:py-3.5"><Rocket className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t("hero.cta")}</button>
                  <button onClick={openLogin} className="btn-outline-hero text-xs w-full sm:w-auto min-h-[32px] rounded-xl px-3 py-1.5 md:text-base md:min-h-[48px] md:rounded-2xl md:px-6 md:py-3.5"><LogIn className="w-3.5 h-3.5 md:w-4 md:h-4" /> Déjà un compte, Connexion</button>
                  <a href="/chat" className="btn-outline-hero text-xs w-full sm:w-auto min-h-[32px] rounded-xl px-3 py-1.5 md:text-base md:min-h-[48px] md:rounded-2xl md:px-6 md:py-3.5"><PlayCircle className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t("hero.ctaDemo")}</a>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-medium flex-wrap justify-center lg:justify-start">
              <span className="flex gap-0.5 text-base leading-none" aria-hidden="true">
                <span>🇫🇷</span><span>🇧🇪</span><span>🇨🇭</span><span>🇨🇦</span><span>🇸🇳</span><span>🇨🇮</span><span>🇨🇬</span>
              </span>
              <span>12 pays francophones couverts</span>
            </div>

            <div className="flex flex-wrap gap-5 sm:gap-8 pt-1 justify-center lg:justify-start">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className="text-2xl sm:text-3xl font-black text-gradient">{stat.value}</span>
                  <span className="text-sm text-muted-foreground font-semibold">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground flex-wrap justify-center lg:justify-start">
              <span className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
                <span className="ml-1 font-bold text-foreground">{t("hero.rating")}</span>
              </span>
              <span>•</span>
              <span>{t("hero.freeStart")}</span>
              <span>•</span>
              <span>{t("hero.noCard")}</span>
            </div>
          </div>

          <div className="relative flex justify-center items-center mt-4 lg:mt-0">
            <div className="relative w-full max-w-sm sm:max-w-xl lg:max-w-2xl mx-auto">
              <img
                src="/hero-characters.webp"
                alt="KelasAI"
                width={1280}
                height={853}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="w-full h-auto animate-float drop-shadow-2xl"
              />
              <div className="absolute top-[8%] -right-2 md:right-0 backdrop-blur-sm px-4 py-2.5 rounded-2xl animate-float flex items-center gap-2 border" style={{ background: "hsl(var(--accent) / 0.95)", borderColor: "hsl(var(--accent) / 0.3)", boxShadow: "var(--shadow-card)" }}>
                <span className="text-lg">⭐</span>
                <div>
                  <p className="text-xs font-black" style={{ color: "hsl(var(--accent-foreground))" }}>{t("hero.points")}</p>
                  <p className="text-xs font-semibold opacity-70" style={{ color: "hsl(var(--accent-foreground))" }}>{t("hero.exerciseDone")}</p>
                </div>
              </div>
              <div className="absolute bottom-[22%] -left-4 md:-left-8 bg-card backdrop-blur-sm px-4 py-2.5 rounded-2xl animate-float-2 border border-border" style={{ boxShadow: "var(--shadow-card)" }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm" style={{ background: "var(--gradient-green)" }}>✓</div>
                  <div>
                    <p className="text-xs font-black text-foreground">{t("hero.corrected")}</p>
                    <p className="text-xs text-muted-foreground font-semibold">{t("hero.mathematics")}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-[52%] -right-1 md:right-2 bg-card backdrop-blur-sm px-3 py-2 rounded-2xl animate-float-3 border border-border" style={{ boxShadow: "var(--shadow-card)" }}>
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-xs font-black text-foreground">{t("hero.level")}</p>
                    <p className="text-xs text-muted-foreground">{t("hero.researcher")}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-[10%] left-2 w-10 h-10 rounded-2xl flex items-center justify-center text-base font-black animate-float-2" style={{ background: "hsl(var(--primary) / 0.12)", color: "hsl(var(--primary))" }}>∑</div>
              <div className="absolute bottom-[12%] right-[8%] w-10 h-10 rounded-2xl flex items-center justify-center text-base animate-float-3" style={{ background: "hsl(var(--success) / 0.12)", color: "hsl(var(--success))" }}>⚛</div>
              <div className="absolute top-[40%] left-[3%] w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black animate-float" style={{ background: "hsl(var(--secondary) / 0.12)", color: "hsl(var(--secondary))" }}>π</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 30 C360 60 1080 0 1440 30 L1440 60 L0 60 Z" fill="hsl(var(--muted))" fillOpacity="0.5" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
